import { Button } from '@mui/material';
import { useForm } from 'react-hook-form';

import { FormErrorLogger } from '@/components/Form/FormErrorLogger';
import { TextInput } from '@/components/Form/TextInput';
import { ActionBar } from '@/components/Layout';

import { CURRENCY_SIGN } from '@/constants/countries';
import { AMOUNT_PATTERN } from '@/constants/patterns';

import { useAppSelector } from '@/hooks/redux';
import { useQuestionInfoMutation } from '@/hooks/useQuestionInfoMutation';
import { useTranslate } from '@/hooks/useTranslate';

import { selectConfigsState } from '@/redux/configs/selectors';
import { selectUserData } from '@/redux/user-data/selectors';

import { getDataTestId } from '@/utils/browser';
import { getUserCurrency } from '@/utils/user';

import { QuestionProps } from './QuestionDrawer.interface';
import { QuestionForm } from './QuestionForm';

export function QuestionAboutMonthlyIncome({
  onAnswered,
  question,
  ...props
}: Readonly<QuestionProps>) {
  const { t } = useTranslate();
  const { user, accountTypeById } = useAppSelector(selectUserData);
  const { defaultCurrency } = useAppSelector(selectConfigsState);

  const userCurrency = user
    ? getUserCurrency(user, { accountTypeById, defaultCurrency })
    : undefined;
  const questionKey = question.extraProps?.questionKey as string | undefined;
  const displayQuestion = questionKey
    ? t(questionKey, {
        currency: userCurrency
          ? CURRENCY_SIGN[userCurrency] || userCurrency
          : '',
      })
    : question.question;
  const {
    handleSubmit,
    control,
    formState: { errors: visibleErrors, isValid },
  } = useForm<{
    monthlyIncome: string;
  }>({
    defaultValues: {
      monthlyIncome: '',
    },
  });

  const { mutate, isPending, error } = useQuestionInfoMutation({
    onFinished: onAnswered,
    target: props.pepAnswerTarget,
  });

  const onSubmit = handleSubmit(async payload => {
    if (!user) return;

    const userCurrency = getUserCurrency(user, {
      accountTypeById,
      defaultCurrency,
    });
    mutate({
      monthlyIncome: parseFloat(payload.monthlyIncome),
      ...(userCurrency ? { monthlyIncomeCurrency: userCurrency } : {}),
    });
  });
  return (
    <QuestionForm
      {...props}
      question={{ ...question, question: displayQuestion }}
      isLoading={isPending}
      answerError={error}
      onSubmit={onSubmit}
    >
      <TextInput
        type="text"
        control={control}
        name="monthlyIncome"
        error={!!visibleErrors.monthlyIncome}
        helperText={visibleErrors.monthlyIncome?.message}
        rules={{
          required: t('questionDrawer.monthlyIncome.monthlyIncome.required'),
          pattern: {
            value: new RegExp(AMOUNT_PATTERN),
            message: t('questionDrawer.monthlyIncome.monthlyIncome.pattern'),
          },
        }}
        placeholder={t(
          'questionDrawer.monthlyIncome.monthlyIncome.placeHolder',
        )}
        inputMode="numeric"
        autoComplete="off"
        disabled={isPending}
      />
      <ActionBar>
        <Button
          {...getDataTestId(`basic-info-proceed-button`)}
          variant="outlined"
          fullWidth
          type="submit"
          disabled={!isValid || isPending}
        >
          {t('questionDrawer.monthlyIncome.button.proceed')}
        </Button>
      </ActionBar>
      <FormErrorLogger
        formName="QuestionAboutMonthlyIncome"
        control={control}
      />
    </QuestionForm>
  );
}
